import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { devWarn } from "@/lib/devLog";
import { Mail, X, Check } from "lucide-react";

export default function EmailVerificationBanner() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!user || user === false || user.email_verified || dismissed) return null;

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      setSent(true);
    } catch (err) {
      // UX: keep the user on the banner if the server rejects (rate-limit,
      // network). The button just re-enables; the error is surfaced in dev.
      devWarn("EmailVerificationBanner: resend failed", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#FBE8CC] border-b border-[#D89A5C]/40" data-testid="verify-banner">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2.5 text-[#7C5621]">
          <Mail size={16} strokeWidth={1.8}/>
          <span className="font-medium">{t("verify_banner.text", { email: user.email })}</span>
        </div>
        <div className="flex items-center gap-2">
          {sent ? (
            <span className="inline-flex items-center gap-1 text-xs font-bold text-[#3F6147]" data-testid="verify-banner-sent">
              <Check size={12}/> {t("verify_banner.sent")}
            </span>
          ) : (
            <button
              onClick={resend}
              disabled={sending}
              className="btn-pill px-3 py-1 text-xs font-bold bg-[#7C5621] text-white hover:bg-[#5C4015] transition disabled:opacity-60"
              data-testid="verify-banner-resend"
            >
              {sending ? "…" : t("verify_banner.resend")}
            </button>
          )}
          <button onClick={() => setDismissed(true)} aria-label="dismiss" className="text-[#7C5621] hover:text-[#3F2A0E]" data-testid="verify-banner-dismiss">
            <X size={14} strokeWidth={2}/>
          </button>
        </div>
      </div>
    </div>
  );
}
